import type {
  LibraryItemKind,
  LibraryItemState,
  LibraryOutcome,
} from '@/lib/api/generated/model';
import type { LibraryStateFilter } from './searchParams';

export type Tone = 'neutral' | 'success' | 'info' | 'warning' | 'destructive';

/** Which fact a badge states, so a caller can drop one without matching words. */
export type StateFact =
  | 'not_installed'
  | 'installed'
  | 'added_by_hand'
  | 'changed'
  | 'update'
  | 'incomplete'
  | 'not_installable';

export interface StateBadgeSpec {
  fact: StateFact;
  label: string;
  tone: Tone;
}

const INSTALLED: StateBadgeSpec = {
  fact: 'installed',
  label: 'Installed',
  tone: 'success',
};
const CHANGED: StateBadgeSpec = {
  fact: 'changed',
  label: 'Changed here',
  tone: 'warning',
};
const UPDATE: StateBadgeSpec = {
  fact: 'update',
  label: 'Update available',
  tone: 'info',
};

/**
 * What a state says, as one badge per fact (spec 104 FR-003).
 *
 * The service answers with a single word for a state that carries as many as
 * three facts: the item is here, it has been changed here, and the library has
 * moved on. Saying only the last of those — "Update available" — left a reader
 * scanning for what they already have with no sign that they have it, and the
 * two facts are independent enough that one badge cannot honestly carry both.
 *
 * So presence comes first and never disappears: an item that is here says so,
 * whatever else is true of it. "Added by hand" is its own presence badge, not a
 * second one, because it says the folder is here and the library did not put it
 * there.
 */
export function stateBadges(
  state: LibraryItemState,
  installable: boolean,
): StateBadgeSpec[] {
  if (!installable) {
    return [
      {
        fact: 'not_installable',
        label: 'Not installable',
        tone: 'destructive',
      },
    ];
  }
  switch (state) {
    case 'not_installed':
      return [
        { fact: 'not_installed', label: 'Not installed', tone: 'neutral' },
      ];
    case 'installed':
      return [INSTALLED];
    case 'added_by_hand':
      return [
        { fact: 'added_by_hand', label: 'Added by hand', tone: 'neutral' },
      ];
    case 'customized':
      return [INSTALLED, CHANGED];
    case 'update_available':
      return [INSTALLED, UPDATE];
    case 'update_available_with_local_changes':
      return [INSTALLED, CHANGED, UPDATE];
  }
}

export function outcomeLabel(outcome: LibraryOutcome): string {
  switch (outcome) {
    case 'added':
      return 'Added';
    case 'already_installed':
      return 'Already installed';
    case 'updated':
      return 'Updated';
    case 'refused':
      return 'Refused';
  }
}

export function kindLabel(kind: LibraryItemKind, plural = false): string {
  const one = { workflow: 'Workflow', agent: 'Agent', skill: 'Skill' }[kind];
  return plural ? `${one}s` : one;
}

/** What each narrowing is called, and what an empty one says (spec 105 FR-001, FR-007). */
export function narrowingLabel(narrowing: LibraryStateFilter): string {
  switch (narrowing) {
    case 'all':
      return 'All';
    case 'installed':
      return 'Installed';
    case 'updates':
      return 'Updates';
    case 'shared':
      return 'Shared';
  }
}

/**
 * Why this tab is empty, when a narrowing is what emptied it.
 *
 * Named after what the reader did rather than after the data: "nothing here is
 * installed" is an answer, "no results" is a shrug.
 */
export function emptyNarrowingSentence(
  narrowing: Exclude<LibraryStateFilter, 'all'>,
  kind: LibraryItemKind,
): string {
  const plural = kindLabel(kind, true).toLowerCase();
  return narrowing === 'installed'
    ? `No ${plural} from the library are installed in this workspace yet.`
    : `No installed ${plural} have an update waiting.`;
}
